import JSZip from "jszip";
import { saveAs } from "file-saver";

// Builds the two Efada letters a student can get, each from a copy of the
// office's own Word letter in public/templates with placeholders where the
// student-specific text goes. Everything else (letterhead, logo, signatures,
// fonts) is kept exactly as in the original.
//
//   efada_template.docx         — الافادة (exam administration → ministry):
//                                 {{EXAM_PARAGRAPHS}} marker paragraph and
//                                 {{STUDENT_WORD}} (الطالب / الطالبة).
//   efada_arabic_template.docx  — افادة باللغة العربية (ACT representative):
//                                 {{STUDENT_NAME}}, {{NATIONAL_ID}}, {{TODAY}}
//                                 and a {{GRADE_PARAGRAPHS}} marker paragraph.
//   mohe_statement_template.docx — MOHE statement (ACT representative → MOHE
//                                 coordination): {{STUDENT_NAME}},
//                                 {{NATIONAL_ID}}, {{TODAY}}, {{STUDENT_WORD}},
//                                 {{FOR_STUDENT}} (للطالب / للطالبة) and a
//                                 {{GRADE_PARAGRAPHS}} marker paragraph.

const templateUrl = (name) => `${process.env.PUBLIC_URL || ""}/templates/${name}`;

const AR_MONTHS = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];

// Wording that changes with the student's gender.
export const GENDER_WORDS = {
  female: { student: "الطالبة", forStudent: "للطالبة", from: "منها", holds: "أنها حاصلة", got: "حاصلة" },
  male: { student: "الطالب", forStudent: "للطالب", from: "منه", holds: "أنه حاصل", got: "حاصل" },
};

// Both letters are dated the day they are issued: "15/9/2026" in the first,
// "15-9-2026" in the second.
export const todayParts = () => {
  const d = new Date();
  return { day: d.getDate(), month: d.getMonth() + 1, year: d.getFullYear() };
};
const todayWith = (sep) => {
  const { day, month, year } = todayParts();
  return [day, month, year].join(sep);
};

// Session wording from the chosen grades' dates, e.g. "يوليو 2024" or
// "أبريل ويونيو 2025" or "يوليو 2024 وأبريل 2025".
export const sessionText = (dates) => {
  const byYear = new Map();
  for (const iso of dates) {
    if (!iso) continue;
    const [y, m] = String(iso).slice(0, 10).split("-").map(Number);
    if (!y || !m) continue;
    if (!byYear.has(y)) byYear.set(y, new Set());
    byYear.get(y).add(m);
  }
  return [...byYear.keys()]
    .sort((a, b) => a - b)
    .map((y) => {
      const months = [...byYear.get(y)].sort((a, b) => a - b).map((m) => AR_MONTHS[m - 1]);
      return `${months.join(" و")} ${y}`;
    })
    .join(" و");
};

// "2026-09-23" → "23 سبتمبر 2026" (the MOHE statement's certificate date).
export const arabicDate = (iso) => {
  const [y, m, d] = String(iso || "").slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "";
  return `${d} ${AR_MONTHS[m - 1]} ${y}`;
};

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// Run/paragraph formats copied from each original letter. `size` is in
// half-points (26 = 13pt, 28 = 14pt); `allBold` is for the Arabic letter,
// where every run is bold. Arabic runs are right-to-left; Latin words
// ("ACT 1", subject names) are bold left-to-right runs.
const runs = ({ size, allBold }) => {
  const SIZE = `<w:sz w:val="${size}"/><w:szCs w:val="${size}"/>`;
  const LANG = '<w:lang w:bidi="ar-EG"/>';
  const BOLD = "<w:b/><w:bCs/>";
  const t = (text) => `<w:t xml:space="preserve">${esc(text)}</w:t>`;
  return {
    ar: (text) =>
      `<w:r><w:rPr><w:rFonts w:cs="Arial" w:hint="cs"/>${allBold ? BOLD : ""}${SIZE}<w:rtl/>${LANG}</w:rPr>${t(text)}</w:r>`,
    arBold: (text) =>
      `<w:r><w:rPr><w:rFonts w:cs="Arial" w:hint="cs"/>${BOLD}${SIZE}<w:rtl/>${LANG}</w:rPr>${t(text)}</w:r>`,
    latinBold: (text) =>
      `<w:r><w:rPr><w:rFonts w:cs="Arial"/>${BOLD}${SIZE}${LANG}</w:rPr>${t(text)}</w:r>`,
    lineBreak: () =>
      `<w:r><w:rPr><w:rFonts w:cs="Arial"/>${allBold ? BOLD : ""}${SIZE}<w:rtl/>${LANG}</w:rPr><w:br/></w:r>`,
    paragraph: (pPrExtra, content) =>
      `<w:p><w:pPr><w:bidi/>${pPrExtra}<w:rPr>${allBold ? BOLD : ""}${SIZE}<w:rtl/>${LANG}</w:rPr></w:pPr>${content.join("")}</w:p>`,
  };
};

// Loads a template, swaps its placeholders and downloads the result.
// `replacements` maps a regex (or placeholder string) to its XML/text.
// Returns { blob, fileName } so the caller can also save a copy to history.
async function fillTemplate(templateName, replacements, fileName) {
  const res = await fetch(templateUrl(templateName));
  if (!res.ok) throw new Error("Could not load the Efada template.");
  const zip = await JSZip.loadAsync(await res.arrayBuffer());

  let xml = await zip.file("word/document.xml").async("string");
  for (const [pattern, value] of replacements) {
    if (typeof pattern === "string" ? !xml.includes(pattern) : !pattern.test(xml)) {
      throw new Error(`Efada template is missing ${pattern}.`);
    }
    // split/join inserts `value` literally (no "$&"-style substitutions).
    xml = xml.split(pattern).join(value);
  }
  zip.file("word/document.xml", xml);

  const blob = await zip.generateAsync({
    type: "blob",
    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
  saveAs(blob, fileName);
  return { blob, fileName };
}

// A whole marker paragraph like <w:p><w:r><w:t>{{NAME}}</w:t></w:r></w:p>.
const markerParagraph = (name) =>
  new RegExp(`<w:p>(?:(?!<w:p>).)*?\\{\\{${name}\\}\\}.*?</w:p>`, "s");

// ── الافادة ──────────────────────────────────────────────────────────────────
// exams: [{ label: "ACT 1", grades: [{subject, score}], session: "يوليو 2024" }]
// The first exam carries the letter's opening (the date and the student's
// name); any following exam uses the "كما يرجى…" wording.
export async function generateEfadaDocument({ studentName, nationalId, gender, exams }) {
  const words = GENDER_WORDS[gender] || GENDER_WORDS.female;
  const { ar, arBold, latinBold, paragraph } = runs({ size: 26, allBold: false });
  const pPr = '<w:ind w:left="26"/><w:jc w:val="lowKashida"/>';

  // "وبدرجة 25 Math وبدرجة 19 Science …"
  const gradeRuns = (grades) =>
    grades.flatMap((g, i) => [
      ar(i === 0 ? "وبدرجة " : " وبدرجة "),
      arBold(`${g.score}  `),
      latinBold(g.subject),
    ]);

  const paragraphs = exams
    .map((exam, i) => {
      const opening =
        i === 0
          ? [
              ar(`إيماءآ إلى كتاب سيادتكم المؤرخ فى ${todayWith("/")} والمتضمن الاستعلام عن صحة الشهادات الخاصة ب${words.student} / `),
              ar(`${studentName}  `),
              ar(`لاستخراج إفادة بالمجموع الاعتبارى وإفادتكم عن صحة الشهادة المقدمة ${words.from} وهى إختبار `),
            ]
          : [ar(`كما يرجى من سيادتكم التكرم بإفادتنا عن صحة الشهادة المقدمة ${words.from} وهى إختبار `)];
      return paragraph(pPr, [
        ...opening,
        latinBold(exam.label),
        ar(" برقم قومى "),
        arBold(nationalId),
        ar("، "),
        ...gradeRuns(exam.grades),
        ar(` حيث ${words.holds} على هذه المحاولات بدور `),
        arBold(exam.session),
        arBold("."),
      ]);
    })
    .join("");

  return fillTemplate(
    "efada_template.docx",
    [
      [markerParagraph("EXAM_PARAGRAPHS"), paragraphs],
      ["{{STUDENT_WORD}}", words.student],
    ],
    `افادة_${nationalId}.docx`
  );
}

// ── افادة باللغة العربية ─────────────────────────────────────────────────────
// act1: { composite, session, grades } or null; act2: { session, grades } or null.
//   "الطالبة حاصلة على إجمالى درجة 22 من 36 محاولة يوليو 2024 وهى عبارة عن:-
//    حاصلة علي درجة 25 فى مادة Math وحاصلة علي درجة 19 فى مادة Science …"
//   "وحاصلة علي درجة 29 فى مادة Math 1 … حيث أنها حاصلة على هذه المحاولات بدور …"
export async function generateArabicEfadaDocument({ studentName, nationalId, gender, act1, act2 }) {
  const words = GENDER_WORDS[gender] || GENDER_WORDS.female;
  const { ar, latinBold, lineBreak, paragraph } = runs({ size: 28, allBold: true });

  // "حاصلة علي درجة 25 فى مادة Math وحاصلة علي درجة 19 فى مادة Science"
  const subjectRuns = (grades, leadingAnd) =>
    grades.flatMap((g, i) => [
      ar(`${i > 0 ? " " : ""}${i > 0 || leadingAnd ? "و" : ""}${words.got} علي درجة ${g.score} فى مادة `),
      latinBold(g.subject),
    ]);

  const paragraphs = [];
  if (act1) {
    paragraphs.push(
      paragraph("", [
        ar(`${words.student} ${words.got} على إجمالى درجة ${act1.composite} من 36 محاولة ${act1.session} وهى عبارة عن:-`),
        lineBreak(),
        ...subjectRuns(act1.grades, false),
        ar("."),
      ])
    );
  }
  if (act2) {
    paragraphs.push(
      paragraph("", [
        // Continues the ACT 1 sentence ("وحاصلة …"), or opens it on its own.
        ...(act1 ? [] : [ar(`${words.student} `)]),
        ...subjectRuns(act2.grades, Boolean(act1)),
        ar(` حيث ${words.holds} على هذه المحاولات بدور ${act2.session}.`),
      ])
    );
  }

  return fillTemplate(
    "efada_arabic_template.docx",
    [
      [markerParagraph("GRADE_PARAGRAPHS"), paragraphs.join("")],
      ["{{STUDENT_NAME}}", esc(studentName)],
      ["{{NATIONAL_ID}}", esc(nationalId)],
      ["{{TODAY}}", todayWith("-")],
    ],
    `افادة_باللغة_العربية_${nationalId}.docx`
  );
}

// ── MOHE statement ───────────────────────────────────────────────────────────
// The original letter fills its page exactly, so a longer grade list is
// compacted step by step (each step keeps the previous ones) until the letter
// fits on one page again. `maxLines` is the most grade lines (one per subject
// plus one per session) a step still fits on one page with the signature
// clear of the page border — measured by rendering the letter with Word, with
// a long name and session. (The margins stay as in the original: the
// signature image hangs below the date, into the bottom margin.)
//   gap   keep the two blank lines above the signature
//   size  grade text size, half-points (28 = 14pt, the original)
//   flow  subjects run on as one sentence per exam instead of one per line
const MOHE_LAYOUTS = [
  { maxLines: 4, gap: true, size: 28, flow: false }, // the original
  { maxLines: 6, gap: false, size: 28, flow: false },
  { maxLines: 9, gap: false, size: 24, flow: false },
  // Fits 22 lines (about 20 subjects) — more than a student realistically has.
  { maxLines: Infinity, gap: false, size: 24, flow: true },
];
const moheLayoutFor = (lines) => MOHE_LAYOUTS.find((l) => lines <= l.maxLines);

// exams: [{ grades: [{subject, score}], session: "فبراير 2025" }] (ACT 1, then
// ACT 2); certificateDate: "2026-09-23". One subject per line, as in the
// original letter:
//   "الطالبة حاصلة علي درجة 28 فى مادة English
//    وحاصلة علي درجة 21 فى مادة Reading
//    حيث أنها حاصلة على هذه المحاولات بدور فبراير 2025
//    بشهادة صادرة وموثقة بتاريخ 23 سبتمبر 2026 وموثقة من وزارة …"
export async function generateMoheStatementDocument({
  studentName,
  nationalId,
  gender,
  exams,
  certificateDate,
}) {
  const words = GENDER_WORDS[gender] || GENDER_WORDS.female;
  const lines = exams.reduce((n, exam) => n + exam.grades.length + 1, 0);
  const layout = moheLayoutFor(lines);
  const { ar, latinBold, lineBreak, paragraph } = runs({ size: layout.size, allBold: true });

  // Kashida-justified like the original letter: one paragraph whose lines end
  // in line breaks, so Word stretches every line to the full width.
  const JUSTIFY = '<w:jc w:val="lowKashida"/>';
  const subjectRuns = (g, first) => [
    ar(`${first ? `${words.student} ` : "و"}${words.got} علي درجة ${g.score} فى مادة `),
    latinBold(g.subject),
  ];
  const sessionRuns = (exam) => [ar(`حيث ${words.holds} على هذه المحاولات بدور ${exam.session}`)];
  const certificateRuns = [
    ar(
      `بشهادة صادرة وموثقة بتاريخ ${arabicDate(certificateDate)} وموثقة من وزارة التربية والتعليم فى نفس التاريخ السابق.`
    ),
  ];

  const grades = layout.flow
    ? // Long lists: one running sentence per exam, each its own paragraph.
      [
        ...exams.map((exam, e) => [
          ...exam.grades.flatMap((g, i) => [...subjectRuns(g, e === 0 && i === 0), ar(" ")]),
          ...sessionRuns(exam),
        ]),
        certificateRuns,
      ]
        .map((content, i, all) =>
          paragraph(i < all.length - 1 ? `<w:spacing w:after="0"/>${JUSTIFY}` : JUSTIFY, content)
        )
        .join("")
    : // One subject per line, as in the original.
      paragraph(JUSTIFY, [
        ...exams.flatMap((exam, e) => [
          ...exam.grades.flatMap((g, i) => [...subjectRuns(g, e === 0 && i === 0), lineBreak()]),
          ...sessionRuns(exam),
          lineBreak(),
        ]),
        ...certificateRuns,
      ]);

  // The two blank lines above the signature, as in the original letter.
  const { paragraph: blank } = runs({ size: 28, allBold: true });
  const gap = layout.gap ? blank("", []) + blank("", []) : "";

  return fillTemplate(
    "mohe_statement_template.docx",
    [
      [markerParagraph("GRADE_PARAGRAPHS"), grades],
      [markerParagraph("SIGNATURE_GAP"), gap],
      ["{{STUDENT_NAME}}", esc(studentName)],
      ["{{NATIONAL_ID}}", esc(nationalId)],
      ["{{TODAY}}", todayWith("-")],
      ["{{STUDENT_WORD}}", words.student],
      ["{{FOR_STUDENT}}", words.forStudent],
    ],
    `افادة_MOHE_${nationalId}.docx`
  );
}
